// src/components/Inventory/Inventory.jsx
// Main component for Inventory Management page

import React, { useState, useEffect } from 'react';
import inventoryService from '../../services/inventoryService';
import InventoryTable from './InventoryTable';
import InventoryModal from './InventoryModal';
import { showNotification } from '../../utils/notification';
import ConfirmDialog from "../common/ConfirmDialog"; // adjust path if needed
import { PlusCircle } from 'lucide-react';
import './inventory.css';

/**
 * Inventory Component
 * Main page component that manages inventory locations
 * Handles CRUD operations and filtering
 */
const Inventory = ({ toggleSidebar }) => {
  
  // ========== STATE MANAGEMENT ==========
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
  
  // All inventories from the backend
  const [inventories, setInventories] = useState([]);
  
  // Filtered inventories (after applying search/filters)
  const [filteredInventories, setFilteredInventories] = useState([]);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  
  // Saving state (for add/edit operations)
  const [isSaving, setIsSaving] = useState(false);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Currently selected inventory for editing (null for add mode)
  const [selectedInventory, setSelectedInventory] = useState(null);
  
  // Filter values
  const [filters, setFilters] = useState({
    search: '',
    type: ''
  });

  const initialFormData = {
  name: '',
  type: '',
  from: '',
  to: '',
  isActive: true,
};
// Delete confirmation modal
const [isConfirmOpen, setIsConfirmOpen] = useState(false);
const [inventoryToDelete, setInventoryToDelete] = useState(null);

// Optional: Loading state while deleting
const [isDeleting, setIsDeleting] = useState(false);

const handleDeleteClick = (inventory) => {
  setInventoryToDelete(inventory);
  setIsConfirmOpen(true);
}


  // ========== LOAD DATA ON COMPONENT MOUNT ==========
  
  /**
   * Load all inventories from the backend when component first renders
   * This runs once when the component mounts
   */
  useEffect(() => {
    loadInventories();
  }, []); // Empty dependency array means this runs once on mount

  /**
   * Apply filters whenever inventories or filter values change
   */
  useEffect(() => {
    applyFilters();
  }, [inventories, filters]); // Run whenever inventories or filters change

  // ========== DATA LOADING ==========

  /**
   * Fetch all inventories from the backend
   */
  const loadInventories = async () => {
    try {
      setIsLoading(true);
      
      // Call the API service
      const data = await inventoryService.getAll();
      
      // Update state with fetched data
      setInventories(data);
      
      // Show success message
      showNotification(`Loaded ${data.length} inventory locations`, 'success');
      
    } catch (error) {
      console.error('Error loading inventories:', error);
      showNotification(`Failed to load inventories: ${error.message}`, 'error');
      
      // Set empty array on error
      setInventories([]);
      
    } finally {
      // Always stop loading, whether successful or not
      setIsLoading(false);
    }
  };

  // ========== FILTERING ==========

  /**
   * Apply current filters to the inventory list
   */
  const applyFilters = () => {
     const safeInventories = Array.isArray(inventories) ? inventories : []; // ✅ guard
    let filtered = [...safeInventories];

    // Filter by type if selected
    if (filters.type) {
      filtered = filtered.filter(inv => inv.type === filters.type);
    }

    // Filter by search term (searches name and manager)
    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(inv => {
        const name = inv.name?.toLowerCase() || '';
        const manager = inv.manager?.toLowerCase() || '';
        return name.includes(searchLower) || manager.includes(searchLower);
      });
    }

    setFilteredInventories(filtered);
  };

  /**
   * Handle filter input changes
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
  };

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setFilters({
      search: '',
      type: ''
    });
    showNotification('Filters cleared', 'info');
  };

  // ========== MODAL OPERATIONS ==========

  /**
   * Open modal for adding new inventory
   */
  const handleAddNew = () => {
    setSelectedInventory(null); // Clear any selected inventory
    setIsModalOpen(true);
  };

  /**
   * Open modal for editing existing inventory
   */
  const handleEdit = (inventory) => {
    setSelectedInventory(inventory);
    setIsModalOpen(true);
  };

  /**
   * Close the modal
   */
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedInventory(null);
  };

  // ========== CRUD OPERATIONS ==========

  /**
   * Save inventory (create or update)
   * @param {Object} formData - The form data from the modal
   */
  const handleSave = async (formData) => {
  try {
    setIsSaving(true);

    // ✅ Ensure backend-compatible payload
    const payload = {
      ...formData,
      type: formData.type?.toUpperCase(), // WAREHOUSE / VEHICLE
      isActive: formData.isActive,
    };

    if (selectedInventory) {
      await inventoryService.update(selectedInventory.id, payload);
      showNotification(
        `Inventory "${payload.name}" updated successfully!`,
        'success'
      );
    } else {
      await inventoryService.create(payload);
      showNotification(
        `New inventory "${payload.name}" added successfully!`,
        'success'
      );
    }

    await loadInventories();

    

    handleCloseModal();

  } catch (error) {
    console.error('Error saving inventory:', error);
    showNotification(
      `Failed to save: ${error.message}`,
      'error'
    );
  } finally {
    setIsSaving(false);
  }
};

  /**
   * Delete an inventory (Note: Delete button removed from UI as per original design)
   * This function is kept for potential future use
   */
  const confirmDelete = async () => {
  if (!inventoryToDelete) return;

  try {
    setIsDeleting(true);
    await inventoryService.delete(inventoryToDelete.id); // call API

    showNotification("Inventory deleted successfully", "success");

    await loadInventories(); // refresh table
  } catch (error) {
    console.error("Delete failed:", error);
    showNotification(`Failed to delete: ${error.message}`, "error");
  } finally {
    setIsDeleting(false);
    setIsConfirmOpen(false);
    setInventoryToDelete(null);
  }
};

const cancelDelete = () => {
  setIsConfirmOpen(false);
  setInventoryToDelete(null);
};
// ========== PAGINATION LOGIC ==========
const totalPages = Math.ceil(filteredInventories.length / itemsPerPage);
const startIndex = (currentPage - 1) * itemsPerPage;
const endIndex = startIndex + itemsPerPage;
const paginatedInventories = filteredInventories.slice(startIndex, endIndex);

const handlePageChange = (newPage) => {
  if (newPage >= 1 && newPage <= totalPages) {
    setCurrentPage(newPage);
  }
};

const handlePreviousPage = () => {
  if (currentPage > 1) {
    setCurrentPage(currentPage - 1);
  }
};

const handleNextPage = () => {
  if (currentPage < totalPages) {
    setCurrentPage(currentPage + 1);
  }
};

  // ========== RENDER ==========

  return (
    <main className="inventory-main-content">
      
      {/* Page Header */}
      <header className="inventory-content-header">
        <button onClick={toggleSidebar} className="sidebar-toggle-btn">
            ☰
          </button>
        <h2 className="inventory-content-title">Inventory Management</h2>
        <div className="header-actions">
          <button 
            className="inventory-btn inventory-btn-add"
            onClick={handleAddNew}
          >
            <PlusCircle size={18} />
            Add New Inventory
          </button>
        </div>
      </header>

      {/* Filter Controls */}
      <section className="inventory-filter-controls">
        <div className="inventory-filter-group">
          
          {/* Search Input */}
          <input 
            type="text"
            name="search"
            className="inventory-custom-input inventory-search-input"
            placeholder="Search Name or Manager"
            value={filters.search}
            onChange={handleFilterChange}
          />

          {/* Type Filter */}
          <select 
            name="type"
            className="inventory-custom-select"
            value={filters.type}
            onChange={handleFilterChange}
          >
            <option value="">All Types</option>
            <option value="Vehicle">Vehicle</option>
            <option value="Warehouse">Warehouse</option>
          </select>

          {/* Apply Filter Button (filters apply automatically, but kept for UI consistency) */}
          {/* <button 
            className="inventory-btn inventory-btn-primary"
            onClick={applyFilters}
          >
            Apply Filter
          </button> */}

          {/* Clear Filters Button */}
          <button 
            className="inventory-btn inventory-btn-clear"
            onClick={handleClearFilters}
          >
            Clear Filters
          </button>
        </div>
      </section>

      {/* Data Table */}
      <InventoryTable 
        inventories={filteredInventories}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        isLoading={isLoading}
      />
       {/* Pagination Controls */}
      <div className="pagination-controls">
        <button 
          className="btn-page" 
          disabled={currentPage === 1} 
          onClick={handlePreviousPage}
        >
          Previous
        </button>
        
        <div className="page-numbers">
          {currentPage > 2 && (
            <>
              <button className="btn-page-num" onClick={() => handlePageChange(1)}>1</button>
              {currentPage > 3 && <span className="page-ellipsis">...</span>}
            </>
          )}
          
          {currentPage > 1 && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(currentPage - 1)}
            >
              {currentPage - 1}
            </button>
          )}
          
          <button className="btn-page-num active">{currentPage}</button>
          
          {currentPage < totalPages && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(currentPage + 1)}
            >
              {currentPage + 1}
            </button>
          )}
          
          {currentPage < totalPages - 1 && (
            <>
              {currentPage < totalPages - 2 && <span className="page-ellipsis">...</span>}
              <button className="btn-page-num" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
            </>
          )}
        </div>

        <span className="page-info">Page {currentPage} of {totalPages || 1}</span>
        
        <button 
          className="btn-page" 
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={handleNextPage}
        >
          Next
        </button>
      </div>

      {/* Add/Edit Modal */}
      <InventoryModal 
      key={selectedInventory ? selectedInventory.id : 'new'}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        inventory={selectedInventory}
        isSaving={isSaving}
      />
      <ConfirmDialog
        isOpen={isConfirmOpen}
        title="Delete Inventory"
        message={`Are you sure you want to delete "${inventoryToDelete?.name}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
      />
    </main>
  );
};

export default Inventory;